import apiClient from './apiClient';

export interface PayPalPlanInfo {
  id: string;
  name: string;
  status: string;
  billing_cycles: Array<{
    frequency: {
      interval_unit: string;
      interval_count: number;
    };
    tenure_type?: string;
    sequence?: number;
    pricing_scheme?: {
      fixed_price?: {
        value: string;
        currency_code: string;
      };
      pricing_model?: string; // 'FIXED', 'VOLUME', 'GRADUATED'
      tiers?: Array<{
        starting_quantity?: string;
        ending_quantity?: string;
        amount: {
          value: string;
          currency_code: string;
        };
      }>;
      taxes?: {
        percentage: string;
        inclusive: boolean;
      };
    };
  }>;
  description?: string;
  product_id?: string;
  payment_preferences?: {
    auto_bill_outstanding?: boolean;
    setup_fee_failure_action?: string;
    payment_failure_threshold?: number;
  };
  usage_type?: string;
  create_time?: string;
  links?: Array<{
    href: string;
    rel: string;
    method: string;
  }>;
  [key: string]: any; // Allow additional PayPal properties
}

export interface AdminSubscriptionPlan {
  id: number;
  tier: string;
  billing_cycle: string;
  plan_type: string;
  paypal_plan_id: string;
  discount_percentage?: number;
  discount_cause?: string;
  is_active: boolean;
  description?: string;
  tax_percentage?: number;
  tax_inclusive: boolean;
  quantity_supported: boolean;
  paypal_update_time?: string;
  paypal_status?: string;
  paypal_price?: number;
  created_at: string;
  updated_at: string;
}

export interface AdminListPlansResponse {
  db_plans: AdminSubscriptionPlan[];
  paypal_plans: PayPalPlanInfo[];
  total_db_plans: number;
  total_paypal_plans: number;
}

export interface TierPlan {
  id: number;
  paypal_plan_id: string;
  tier: string;
  price?: number;
  base_price?: number | null;
  discounted_price?: number | null;
  currency_code?: string;
  is_active: boolean;
  is_base: boolean;
  is_current: boolean;
  description?: string;
  paypal_status?: string;
  pricing_model?: string;
}

export interface UnifiedPlan {
  name: string;
  currency_code: string;
  is_base: boolean;
  is_current: boolean;
  is_active: boolean;
  is_hidden: boolean;
  monthly_plans: { [tier: string]: TierPlan }; // Dict keyed by tier name (free, starter, pro, family)
  annual_plans: { [tier: string]: TierPlan }; // Dict keyed by tier name (free, starter, pro, family)
  monthly_tiers_count: number;
  annual_tiers_count: number;
  has_all_monthly_tiers: boolean;
  has_all_annual_tiers: boolean;
  monthly_subscriber_count?: number;
  annual_subscriber_count?: number;
  total_subscribers?: number;
}

export interface UnifiedPlansResponse {
  plans: UnifiedPlan[];
  total: number;
}

export interface PlanTier {
  tier_name: string;
  sequence: number;
  starting_quantity: string;
  ending_quantity: string | null;
  amount: number;
  currency_code: string;
}

export interface DetailedTierPlan {
  id: number;
  paypal_plan_id: string;
  tier: string;
  billing_cycle: string;
  price?: number;
  base_price?: number | null;
  discounted_price?: number | null;
  discount_percentage?: number;
  twelve_x_monthly_price?: number | null; // 12x monthly price (for annual plans)
  annual_savings_percentage?: number | null; // Savings percentage vs 12x monthly (for annual plans)
  currency_code?: string;
  is_active: boolean;
  is_base: boolean;
  is_current: boolean;
  description?: string;
  discount_cause?: string;
  tax_percentage?: number;
  tax_inclusive: boolean;
  quantity_supported: boolean;
  paypal_status?: string;
  pricing_model?: string;
  paypal_plan: any;
  billing_cycles?: any[];
  regular_billing_cycle?: {
    sequence?: number;
    frequency?: {
      interval_unit: string;
      interval_count: number;
    };
    total_cycles?: number;
    pricing_scheme?: any;
  };
  trial_periods?: Array<{
    sequence?: number;
    frequency?: {
      interval_unit: string;
      interval_count: number;
    };
    total_cycles?: number;
    price?: number;
    currency_code?: string;
    pricing_scheme?: any;
  }>;
  setup_fee?: number;
  setup_fee_failure_action?: string;
  payment_preferences?: {
    auto_bill_outstanding?: boolean;
    payment_failure_threshold?: number;
    setup_fee_failure_action?: string;
  };
}

export interface PlanDetailsResponse {
  name: string;
  monthly_plans: { [tier: string]: DetailedTierPlan }; // Dict keyed by tier name
  annual_plans: { [tier: string]: DetailedTierPlan }; // Dict keyed by tier name
  monthly_subscribers: Array<{ id: string; email: string }>;
  annual_subscribers: Array<{ id: string; email: string }>;
  total_subscribers: number;
}

export interface TierPricing {
  tier_name: string;
  starting_quantity: string;
  ending_quantity?: string;
  amount: number;
  currency_code?: string;
}

export interface TrialPeriod {
  price: number;
  interval_unit: string; // 'DAY', 'WEEK', 'MONTH', 'YEAR'
  interval_count: number;
  cycles: number;
}

export interface BasePlan {
  id: number;
  tier: string;
  billing_cycle: string;
  plan_type: string;
  paypal_plan_id: string;
  description?: string;
  is_base: boolean;
}

export interface CreateTieredPlanRequest {
  name: string;
  description: string;
  currency_code?: string;
  subscription_tier?: string;
  has_setup_fee?: boolean;
  setup_fee?: number;
  has_trial?: boolean;
  trial_periods?: TrialPeriod[];
  billing_cycle: string;
  interval_count?: number;
  tiers: TierPricing[];
  unlimited_cycles?: boolean;
  total_cycles?: number;
  tax_calculation?: string;
  tax_percentage?: number;
  payment_failure_threshold?: number;
  auto_bill_outstanding?: boolean;
  setup_fee_failure_action?: string;
  discount_type?: string; // 'base' or discount percentage as string (e.g., '25')
  base_plan_id?: number;
  discount_cause?: string;
}

export interface UpdatePlanRequest {
  discount_cause?: string;
  is_active?: boolean;
  is_current?: boolean;
  description?: string;
  is_hidden?: boolean;
}

export interface FreeUpgradeSetting {
  id: number;
  source_tier: string;
  destination_tier: string;
  billing_cycle: string;
  free_upgrade_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface GetFreeUpgradeSettingsResponse {
  settings: FreeUpgradeSetting[];
}

export interface UpdateFreeUpgradeSettingRequest {
  free_upgrade_enabled: boolean;
}

export interface AddTierRequest {
  subscription_tier: string;
  monthly_base_price: number;
  monthly_discounted_price?: number;
  annual_base_price: number;
  annual_discounted_price?: number;
  description?: string;
  discount_cause?: string;
}

export const adminPaymentService = {
  listAllPlans: async (): Promise<AdminListPlansResponse> => {
    const response = await apiClient.get('/payment/admin/subscription-plans');
    return response.data.data;
  },

  listUnifiedPlans: async (): Promise<UnifiedPlansResponse> => {
    const response = await apiClient.get('/payment/admin/subscription-plans/unified');
    return response.data.data;
  },

  getPlanByName: async (planName: string): Promise<PlanDetailsResponse> => {
    const response = await apiClient.get(`/payment/admin/subscription-plans/by-name/${encodeURIComponent(planName)}`);
    return response.data.data;
  },

  createTieredPlan: async (planData: CreateTieredPlanRequest): Promise<any> => {
    const response = await apiClient.post('/payment/admin/subscription-plans/tiered', planData);
    return response.data.data;
  },

  updatePlan: async (planId: number, planData: UpdatePlanRequest): Promise<any> => {
    const response = await apiClient.patch(`/payment/admin/subscription-plans/${planId}`, planData);
    return response.data;
  },

  updatePayPalPlan: async (planId: number, planData: {
    name?: string;
    description?: string;
    payment_preferences?: {
      auto_bill_outstanding?: boolean;
      setup_fee?: {
        value: string;
        currency_code: string;
      };
      setup_fee_failure_action?: string;
      payment_failure_threshold?: number;
    };
    taxes?: {
      percentage?: string;
      inclusive?: boolean;
    };
  }): Promise<any> => {
    const response = await apiClient.patch(`/payment/admin/subscription-plans/${planId}/paypal`, planData);
    return response.data;
  },

  updateUnifiedPlanBaseInfo: async (planName: string, planData: {
    name?: string;
    description?: string;
    payment_preferences?: {
      auto_bill_outstanding?: boolean;
      setup_fee?: {
        value: string;
        currency_code: string;
      };
      setup_fee_failure_action?: string;
      payment_failure_threshold?: number;
    };
    taxes?: {
      percentage?: string;
      inclusive?: boolean;
    };
  }): Promise<any> => {
    const response = await apiClient.patch(`/payment/admin/subscription-plans/by-name/${encodeURIComponent(planName)}/base-info`, planData);
    return response.data;
  },

  setActivePlan: async (planId: number): Promise<any> => {
    const response = await apiClient.post(`/payment/admin/subscription-plans/${planId}/set-active`, {});
    return response.data.data;
  },

  deletePlan: async (planId: number): Promise<any> => {
    const response = await apiClient.delete(`/payment/admin/subscription-plans/${planId}`);
    return response.data.data;
  },

  getPaypalPlanDetails: async (paypalPlanId: string): Promise<PayPalPlanInfo> => {
    const response = await apiClient.get(`/payment/admin/paypal-plans/${paypalPlanId}`);
    return response.data.data;
  },

  getBasePlans: async (): Promise<BasePlan[]> => {
    const response = await apiClient.get('/payment/admin/subscription-plans/base');
    return response.data.data;
  },

  createMultiplePlans: async (plansData: {
    monthly_plan?: CreateTieredPlanRequest;
    annual_plan?: CreateTieredPlanRequest;
    shared_name?: string;
    shared_description?: string;
    currency_code: string;
  }): Promise<any> => {
    const response = await apiClient.post('/payment/admin/subscription-plans/multiple', plansData);
    return response.data.data;
  },

  activatePlan: async (planId: number): Promise<any> => {
    const response = await apiClient.post(`/payment/admin/subscription-plans/${planId}/activate`);
    return response.data.data;
  },

  deactivatePlan: async (planId: number): Promise<any> => {
    const response = await apiClient.post(`/payment/admin/subscription-plans/${planId}/deactivate`);
    return response.data.data;
  },

  activateUnifiedPlan: async (planName: string): Promise<any> => {
    const encodedPlanName = encodeURIComponent(planName);
    const response = await apiClient.post(`/payment/admin/subscription-plans/unified/${encodedPlanName}/activate`);
    return response.data.data;
  },

  deactivateUnifiedPlan: async (planName: string): Promise<any> => {
    const encodedPlanName = encodeURIComponent(planName);
    const response = await apiClient.post(`/payment/admin/subscription-plans/unified/${encodedPlanName}/deactivate`);
    return response.data.data;
  },

  setCurrentPlan: async (planId: number): Promise<any> => {
    const response = await apiClient.post(`/payment/admin/subscription-plans/${planId}/set-current`);
    return response.data.data;
  },

  getCurrentPlan: async (): Promise<{ current_plan: { id: number; plan_name: string; paypal_plan_id: string; tier: string; billing_cycle: string } | null }> => {
    const response = await apiClient.get('/payment/admin/subscription-plans/current');
    return response.data.data;
  },

  getFreeUpgradeSettings: async (): Promise<GetFreeUpgradeSettingsResponse> => {
    const response = await apiClient.get('/payment/admin/free-upgrade-settings');
    return response.data.data;
  },

  getFreeUpgradeSetting: async (sourceTier: string, destinationTier: string, billingCycle: string): Promise<FreeUpgradeSetting> => {
    const response = await apiClient.get(`/payment/admin/free-upgrade-settings/${sourceTier}/${destinationTier}/${billingCycle}`);
    return response.data.data;
  },

  updateFreeUpgradeSetting: async (
    sourceTier: string,
    destinationTier: string,
    billingCycle: string,
    settingData: UpdateFreeUpgradeSettingRequest
  ): Promise<FreeUpgradeSetting> => {
    const response = await apiClient.put(
      `/payment/admin/free-upgrade-settings/${sourceTier}/${destinationTier}/${billingCycle}`,
      settingData
    );
    return response.data.data;
  },

  addTierToUnifiedPlan: async (planName: string, tierData: AddTierRequest): Promise<any> => {
    const response = await apiClient.post(
      `/payment/admin/subscription-plans/unified/${encodeURIComponent(planName)}/add-tier`,
      tierData
    );
    return response.data.data;
  },

};

